class ProductStitcher extends HTMLElement {
  constructor() {
    super();
    this.selectors = {
      mainProduct: "[js-main-product]",
      productMetaImageWithText: "[js-product-meta-image-with-text]",
      productMetaAccordions: "[js-product-meta-accordions]",
      productBenefits: "[js-product-benefits]",
      productComparison: "[js-product-comparison]",
      junipProductReview: "[js-junip-product-review]",
      productMetaSteps: "[js-product-meta-steps]",
      productRecommendations: "[js-product-recommendations]"
    };
    this.sections = {};
    this.cache = new Map();
    this.abortController = null;

    this.isProductPage =
      (window.ShopifyAnalytics?.meta?.page?.pageType === "product") ||
      /\/products\//.test(window.location.pathname);

    Object.keys(this.selectors).forEach((t) => {
      this.sections[t] = document.querySelector(this.selectors[t]);
    });

    this.currentHandle = this.#readCurrentHandle();

    this.onItemClick = this.onItemClick.bind(this);
    this.onConnected = this.onConnected.bind(this);

    this.bindItems();
    document.addEventListener("click", this.onConnected, true);
    document.addEventListener("change", this.onConnected, true);
  }

  #readCurrentHandle() {
    return (
      window.ShopifyAnalytics?.meta?.product?.handle ||
      document.querySelector("variant-picker[data-product-handle]")?.dataset.productHandle ||
      null
    );
  }

  #handleFromUrl(url) {
    if (!url) return null;
    return url.split("?")[0].replace(/\/+$/, "").split("/").pop() || null;
  }

  onConnected(e) {
    const src =
      (e.target.getAttribute && e.target.getAttribute("data-connected-product-url") ? e.target : null) ||
      (e.target.closest && e.target.closest("label")?.querySelector("[data-connected-product-url]")) ||
      (e.target.closest && e.target.closest("[data-connected-product-url]"));
    if (!src) return;

    const handle = this.#handleFromUrl(src.getAttribute("data-connected-product-url"));
    if (!handle || handle === this.currentHandle) return; // same product → let Horizon handle the variant

    e.preventDefault();
    e.stopImmediatePropagation();

    this.currentHandle = handle;
    if (this.isProductPage) this.updateURL(handle);
    this.loadProduct(handle);
  }

  bindItems() {
    this.querySelectorAll("[js-stitcher-item]").forEach((item) => {
      if (item._stitchBound) return;
      item._stitchBound = true;
      item.addEventListener("click", this.onItemClick, true);
    });
  }

  onItemClick(e) {
    const item = e.currentTarget;
    const handle = item.dataset.productHandle;
    if (!handle) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    this.currentHandle = handle;
    this.querySelectorAll("[js-stitcher-item]").forEach((i) => i.classList.toggle("active", i === item));
    if (this.isProductPage) this.updateURL(handle);
    this.loadProduct(handle);
    if (item.dataset.productTitle) this.updateTitle(item.dataset.productTitle);
  }

  updateURL(t) {
    window.history.replaceState({}, "", `/products/${t}`);
  }

  loadProduct(t) {
    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.sections.mainProduct?.classList.add("section--loading");

    const jobs = Object.entries(this.sections)
      .filter(([, el]) => el)
      .map(([key, el]) => {
        const sectionId = el.dataset.sectionId;
        if (!sectionId) return null;
        return this.renderSection({ key, url: `/products/${t}?section_id=${sectionId}`, container: el, signal });
      })
      .filter(Boolean);

    Promise.all(jobs)
      .catch((err) => { if (err && err.name !== "AbortError") console.warn("[product-stitcher]", err); })
      .finally(() => {
        this.sections.mainProduct?.classList.remove("section--loading");
        this.currentHandle = this.#readCurrentHandle() || this.currentHandle;
        this.bindItems();
        this.dispatchInitEvents();
      });
  }

  updateTitle(t) {
    const pageTitle = document.querySelector("title");
    if (pageTitle) pageTitle.textContent = t;
    const mediaTitle = document.querySelector("[js-product-media-title]");
    if (mediaTitle) mediaTitle.textContent = t.replace(/^\d+k\s+/i, "");
  }

  async renderSection({ key, url, container, signal }) {
    if (this.cache.has(url)) { container.innerHTML = this.cache.get(url); return; }
    try {
      const res = await fetch(url, { signal });
      if (!res.ok) return;
      const text = await res.text();
      const node = new DOMParser().parseFromString(text, "text/html").querySelector(this.selectors[key]);
      if (!node) return;
      const html = node.innerHTML;
      this.cache.set(url, html);
      container.innerHTML = html;
    } catch (err) {
      if (err && err.name !== "AbortError") console.warn("[product-stitcher]", key, err);
    }
  }

  dispatchInitEvents() {
    [
      "accordions:init", "faqs-marquee:init", "faq-popup-utils:init",
      "icon-with-content:init", "lazy-video:init", "product-IRL:init",
      "product-comparison-marquee:init", "ingredient-popup-utils:init",
      "custom-badge-text-slider:init", "rebuy:init"
    ].forEach((ev) => document.dispatchEvent(new CustomEvent(ev)));
  }
}

if (!customElements.get("product-stitcher")) {
  customElements.define("product-stitcher", ProductStitcher);
}